import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { X, Plus, Trash2, Palette } from 'lucide-react';
import HolisticReportCardPreview from './HolisticReportCardPreview';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { saveHolisticReportCard, updateHolisticReportCard } from '../../firebase/reportCards';
import toast from 'react-hot-toast';

// Color themes for report cards
const colorThemes = {
  indigo: {
    name: 'Indigo & Pink',
    primary: [79, 70, 229],
    primaryHex: '#4F46E5',
    secondary: '#EC4899',
    tableHeader: [224, 231, 255],
    accent: [79, 70, 229],
  },
  emerald: {
    name: 'Emerald & Amber',
    primary: [16, 185, 129],
    primaryHex: '#10B981',
    secondary: '#F59E0B',
    tableHeader: [209, 250, 229],
    accent: [16, 185, 129],
  },
  blue: {
    name: 'Blue & Orange',
    primary: [59, 130, 246],
    primaryHex: '#3B82F6',
    secondary: '#F97316',
    tableHeader: [219, 234, 254],
    accent: [59, 130, 246],
  },
  purple: {
    name: 'Purple & Teal',
    primary: [139, 92, 246],
    primaryHex: '#8B5CF6',
    secondary: '#14B8A6',
    tableHeader: [237, 233, 254],
    accent: [139, 92, 246],
  },
  rose: {
    name: 'Rose & Cyan',
    primary: [244, 63, 94],
    primaryHex: '#F43F5E',
    secondary: '#06B6D4',
    tableHeader: [255, 228, 230],
    accent: [244, 63, 94],
  },
  slate: {
    name: 'Slate & Lime',
    primary: [71, 85, 105],
    primaryHex: '#475569',
    secondary: '#84CC16',
    tableHeader: [241, 245, 249],
    accent: [71, 85, 105],
  },
  amber: {
    name: 'Amber & Violet',
    primary: [217, 119, 6],
    primaryHex: '#D97706',
    secondary: '#7C3AED',
    tableHeader: [254, 243, 199],
    accent: [217, 119, 6],
  },
  teal: {
    name: 'Teal & Rose',
    primary: [20, 184, 166],
    primaryHex: '#14B8A6',
    secondary: '#E11D48',
    tableHeader: [204, 251, 241],
    accent: [20, 184, 166],
  },
};

const HolisticReportCardForm = ({ showForm, setShowForm, batches, students, loadStudents, userProfile, editingReport, onReportUpdated }) => {
  const [formData, setFormData] = useState({
    studentId: '',
    batchId: '',
    class: '',
    section: '',
    rollNumber: '',
    dateOfBirth: '',
    academicSession: `${new Date().getFullYear()}-${new Date().getFullYear() + 1}`,
    reportType: 'exam', // 'exam' or 'final'
    examName: '',
    subjects: [{ name: '', marks: '', maxMarks: '', grade: '', remarks: '' }],
    finalReport: {
      exams: [{ name: '', maxMarks: '' }],
      subjects: [{ name: '', marks: [] }],
    },
    summary: {
        totalMarksObtained: 0,
        totalMaximumMarks: 0,
        percentage: 0,
        overallGrade: '',
        rank: '',
    },
    coScholastic: {
        discipline: '',
        communicationSkills: '',
        participation: '',
        teamwork: '',
        leadership: '',
        creativity: '',
    },
    skills: {
        criticalThinking: '',
        problemSolving: '',
        analyticalSkills: '',
        collaboration: '',
        initiative: '',
    },
    attendance: {
        totalWorkingDays: '',
        daysPresent: '',
        percentage: 0,
    },
    footer: {
        teacherRemarks: '',
        teacherName: '',
        principalRemarks: '',
        principalName: '',
    },
  });
  const [selectedBatch, setSelectedBatch] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [selectedTheme, setSelectedTheme] = useState('indigo');
  const [isSaving, setIsSaving] = useState(false);
  const [debugLogs, setDebugLogs] = useState([]);

  // Initialize form with editing data
  useEffect(() => {
    if (editingReport) {
      setFormData({
        studentId: editingReport.studentId || '',
        batchId: editingReport.batchId || '',
        class: editingReport.class || '',
        section: editingReport.section || '',
        rollNumber: editingReport.rollNumber || '',
        dateOfBirth: editingReport.dateOfBirth || '',
        academicSession: editingReport.academicSession || `${new Date().getFullYear()}-${new Date().getFullYear() + 1}`,
        reportType: editingReport.reportType || 'exam',
        examName: editingReport.examName || '',
        subjects: editingReport.subjects || [{ name: '', marks: '', maxMarks: '', grade: '', remarks: '' }],
        finalReport: editingReport.finalReport || {
          exams: [{ name: '', maxMarks: '' }],
          subjects: [{ name: '', marks: [] }],
        },
        summary: editingReport.summary || {
          totalMarksObtained: 0,
          totalMaximumMarks: 0,
          percentage: 0,
          overallGrade: '',
          rank: '',
        },
        coScholastic: editingReport.coScholastic || {
          discipline: '',
          communicationSkills: '',
          participation: '',
          teamwork: '',
          leadership: '',
          creativity: '',
        },
        skills: editingReport.skills || {
          criticalThinking: '',
          problemSolving: '',
          analyticalSkills: '',
          collaboration: '',
          initiative: '',
        },
        attendance: editingReport.attendance || {
          totalWorkingDays: '',
          daysPresent: '',
          percentage: 0,
        },
        footer: editingReport.footer || {
          teacherRemarks: '',
          teacherName: '',
          principalRemarks: '',
          principalName: '',
        },
      });
      setSelectedBatch(editingReport.batchId || '');
    }
  }, [editingReport]);

  useEffect(() => {
    if (selectedBatch) {
      loadStudents(selectedBatch);
    }
  }, [selectedBatch]);

  useEffect(() => {
    let totalMarksObtained = 0;
    let totalMaximumMarks = 0;

    if (formData.reportType === 'exam') {
        formData.subjects.forEach(subject => {
            totalMarksObtained += Number(subject.marks) || 0;
            totalMaximumMarks += Number(subject.maxMarks) || 0;
        });
    } else {
        formData.finalReport.subjects.forEach(subject => {
            totalMarksObtained += subject.marks.reduce((acc, mark) => acc + (Number(mark) || 0), 0);
        });
        totalMaximumMarks = formData.finalReport.exams.reduce((acc, exam) => acc + (Number(exam.maxMarks) || 0), 0) * formData.finalReport.subjects.length;
    }

    const percentage = totalMaximumMarks > 0 ? (totalMarksObtained / totalMaximumMarks) * 100 : 0;

    setFormData(prevData => ({
        ...prevData,
        summary: {
            ...prevData.summary,
            totalMarksObtained,
            totalMaximumMarks,
            percentage: percentage.toFixed(2),
        }
    }));
}, [formData.subjects, formData.finalReport, formData.reportType]);

useEffect(() => {
    const totalDays = Number(formData.attendance.totalWorkingDays) || 0;
    const presentDays = Number(formData.attendance.daysPresent) || 0;
    const percentage = totalDays > 0 ? (presentDays / totalDays) * 100 : 0;
    setFormData(prevData => ({
        ...prevData,
        attendance: {
            ...prevData.attendance,
            percentage: percentage.toFixed(2),
        }
    }));
}, [formData.attendance.totalWorkingDays, formData.attendance.daysPresent]);

  const handleBatchChange = (e) => {
    const batchId = e.target.value;
    setSelectedBatch(batchId);
    setFormData({ ...formData, batchId, studentId: '' });
  };

  const handleStudentChange = (e) => {
    const studentId = e.target.value;
    const student = students.find(s => s.id === studentId);
    setFormData({
      ...formData,
      studentId,
      dateOfBirth: student?.dob || '',
    });
  };

  const handleAddSubject = () => {
    if (formData.reportType === 'exam') {
      setFormData({
        ...formData,
        subjects: [...formData.subjects, { name: '', marks: '', maxMarks: '', grade: '', remarks: '' }],
      });
    } else {
      setFormData({
        ...formData,
        finalReport: {
          ...formData.finalReport,
          subjects: [...formData.finalReport.subjects, { name: '', marks: Array(formData.finalReport.exams.length).fill('') }],
        },
      });
    }
  };

  const handleRemoveSubject = (index) => {
    if (formData.reportType === 'exam') {
      const subjects = [...formData.subjects];
      subjects.splice(index, 1);
      setFormData({ ...formData, subjects });
    } else {
      const subjects = [...formData.finalReport.subjects];
      subjects.splice(index, 1);
      setFormData({ ...formData, finalReport: { ...formData.finalReport, subjects } });
    }
  };

  const handleAddExam = () => {
    setFormData({
      ...formData,
      finalReport: {
        ...formData.finalReport,
        exams: [...formData.finalReport.exams, { name: '', maxMarks: '' }],
      },
    });
  };

  const handleRemoveExam = (index) => {
    const exams = [...formData.finalReport.exams];
    exams.splice(index, 1);
    setFormData({ ...formData, finalReport: { ...formData.finalReport, exams } });
  };

    const generatePDF = async () => {
    setDebugLogs(prev => [...prev, 'generatePDF function called']);
    setIsSaving(true);
    try {
      setDebugLogs(prev => [...prev, editingReport ? 'Updating report card data...' : 'Saving report card data...']);
      const dataToSave = {
        ...formData,
        coachingId: userProfile?.coachingId || '',
      };
      
      if (editingReport) {
        await updateHolisticReportCard(editingReport.id, dataToSave);
        setDebugLogs(prev => [...prev, 'Report card data updated successfully.']);
        if (onReportUpdated) {
          onReportUpdated({ id: editingReport.id, ...dataToSave, studentData: editingReport.studentData });
        }
      } else {
        await saveHolisticReportCard(dataToSave);
        setDebugLogs(prev => [...prev, 'Report card data saved successfully.']);
      }

      setDebugLogs(prev => [...prev, 'Generating PDF...']);
      const doc = new jsPDF();
      const student = students.find(s => s.id === formData.studentId);
      const instituteInfo = userProfile?.reportCardInstituteDefaults || {};

      // Get selected theme colors
      const theme = colorThemes[selectedTheme];
      const primaryColor = theme.primary;
      const primaryColorHex = theme.primaryHex;
      const secondaryColor = theme.secondary;
      const headerTextColor = '#FFFFFF';
      const tableHeaderColor = theme.tableHeader;
      const grayText = [107, 114, 128]; // gray-500

      // Draw Header Background
      doc.setFillColor(...primaryColor);
      doc.rect(0, 0, 210, 40, 'F');
      
      // Institute Name
      doc.setFontSize(20);
      doc.setTextColor(headerTextColor);
      doc.setFont('helvetica', 'bold');
      doc.text(userProfile?.coachingName || 'Institute Name', 105, 15, { align: 'center' });
      
      // Institute Details
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      if (instituteInfo.address) {
        doc.text(instituteInfo.address, 105, 23, { align: 'center' });
      }
      let contactLine = [];
      if (instituteInfo.institutePhone) contactLine.push(`Phone: ${instituteInfo.institutePhone}`);
      if (instituteInfo.instituteEmail) contactLine.push(`Email: ${instituteInfo.instituteEmail}`);
      if (contactLine.length > 0) {
        doc.text(contactLine.join(' | '), 105, 29, { align: 'center' });
      }
      if (instituteInfo.affiliationNumber) {
        doc.text(`Affiliation No: ${instituteInfo.affiliationNumber}`, 105, 35, { align: 'center' });
      }

      let y = 50;
      const leftCol = 14;
      const midCol = 105;
      const lineHeight = 6;

      // Helper function for section headers
      const addSectionHeader = (title, yPos) => {
        doc.setFontSize(12);
        doc.setTextColor(primaryColorHex);
        doc.setFont('helvetica', 'bold');
        doc.text(title, leftCol, yPos);
        doc.setDrawColor(secondaryColor);
        doc.line(leftCol, yPos + 2, 196, yPos + 2);
        return yPos + 10;
      };

      // Helper function for label-value pairs
      const addLabelValue = (label, value, x, yPos, labelWidth = 35) => {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9);
        doc.setTextColor(0, 0, 0);
        doc.text(`${label}:`, x, yPos);
        doc.setFont('helvetica', 'normal');
        doc.text(value || '', x + labelWidth, yPos);
      };

      // 1. Student Details
      y = addSectionHeader('1. Student Details', y);
      doc.setTextColor(0, 0, 0);
      
      addLabelValue('Name', student?.name || student?.displayName || '', leftCol, y, 15);
      addLabelValue('Class', formData.class || '', midCol, y, 15);
      y += lineHeight;
      
      addLabelValue('Section', formData.section || '', leftCol, y, 18);
      addLabelValue('Roll Number', formData.rollNumber || '', midCol, y, 28);
      y += lineHeight;
      
      addLabelValue('Date of Birth', formData.dateOfBirth || '', leftCol, y, 30);
      addLabelValue('Academic Session', formData.academicSession || '', midCol, y, 38);
      y += 12;

      // 2. Academic Performance (with table)
      y = addSectionHeader('2. Academic Performance', y);
      
      if (formData.reportType === 'exam') {
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(0, 0, 0);
        doc.text(`Exam: ${formData.examName || ''}`, leftCol, y);
        y += 6;
        
        autoTable(doc, { 
          startY: y, 
          head: [['Subject', 'Marks Obtained', 'Maximum Marks', 'Grade', 'Remarks']],
          body: formData.subjects.map(subject => [
            subject.name || '',
            subject.marks || '',
            subject.maxMarks || '',
            subject.grade || '',
            subject.remarks || '',
          ]),
          theme: 'grid',
          styles: { fontSize: 9, cellPadding: 3 },
          headStyles: { fillColor: tableHeaderColor, textColor: primaryColor, fontStyle: 'bold' },
          alternateRowStyles: { fillColor: [249, 250, 251] },
        });
      } else {
        const examHeaders = ['Subject', ...formData.finalReport.exams.map(e => `${e.name} (${e.maxMarks})`), 'Total Marks'];
        autoTable(doc, { 
          startY: y, 
          head: [examHeaders],
          body: formData.finalReport.subjects.map(subject => [
            subject.name || '',
            ...subject.marks.map(m => m || ''),
            subject.marks.reduce((a, b) => Number(a) + Number(b), 0),
          ]),
          theme: 'grid',
          styles: { fontSize: 9, cellPadding: 3 },
          headStyles: { fillColor: tableHeaderColor, textColor: primaryColor, fontStyle: 'bold' },
          alternateRowStyles: { fillColor: [249, 250, 251] },
        });
      }
      y = doc.lastAutoTable.finalY + 12;

      // 3. Overall Academic Summary
      y = addSectionHeader('3. Overall Academic Summary', y);
      doc.setTextColor(0, 0, 0);
      
      addLabelValue('Total Marks Obtained', String(formData.summary.totalMarksObtained || '0'), leftCol, y, 45);
      addLabelValue('Total Maximum Marks', String(formData.summary.totalMaximumMarks || '0'), midCol, y, 45);
      // Percentage in blue
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(primaryColorHex);
      doc.text(`Percentage: ${formData.summary.percentage || '0'}%`, 160, y);
      y += lineHeight;
      
      doc.setTextColor(0, 0, 0);
      addLabelValue('Overall Grade', formData.summary.overallGrade || '', leftCol, y, 32);
      addLabelValue('Rank', formData.summary.rank || '', midCol, y, 15);
      y += 12;

      // 4. Co-Scholastic Areas
      y = addSectionHeader('4. Co-Scholastic Areas', y);
      doc.setTextColor(0, 0, 0);
      
      addLabelValue('Discipline', formData.coScholastic.discipline || '', leftCol, y, 25);
      addLabelValue('Communication Skills', formData.coScholastic.communicationSkills || '', midCol - 20, y, 45);
      addLabelValue('Participation', formData.coScholastic.participation || '', 155, y, 30);
      y += lineHeight;
      
      addLabelValue('Teamwork', formData.coScholastic.teamwork || '', leftCol, y, 25);
      addLabelValue('Leadership', formData.coScholastic.leadership || '', midCol - 20, y, 28);
      addLabelValue('Creativity', formData.coScholastic.creativity || '', 155, y, 25);
      y += 12;

      // 5. Skills
      y = addSectionHeader('5. Skills', y);
      doc.setTextColor(0, 0, 0);
      
      addLabelValue('Critical Thinking', formData.skills.criticalThinking || '', leftCol, y, 38);
      addLabelValue('Problem Solving', formData.skills.problemSolving || '', midCol - 20, y, 38);
      addLabelValue('Analytical Skills', formData.skills.analyticalSkills || '', 155, y, 38);
      y += lineHeight;
      
      addLabelValue('Collaboration', formData.skills.collaboration || '', leftCol, y, 30);
      addLabelValue('Initiative', formData.skills.initiative || '', midCol - 20, y, 22);
      y += 12;

      // 6. Attendance
      y = addSectionHeader('6. Attendance', y);
      doc.setTextColor(0, 0, 0);
      
      addLabelValue('Total Working Days', formData.attendance.totalWorkingDays || '', leftCol, y, 42);
      addLabelValue('Days Present', formData.attendance.daysPresent || '', midCol, y, 30);
      addLabelValue('Percentage', `${formData.attendance.percentage || '0'}%`, 155, y, 28);
      y += 20;

      // Footer - Teacher and Principal Remarks
      doc.setDrawColor(200, 200, 200);
      doc.line(leftCol, y - 8, 196, y - 8);

      // Teacher's Remarks (left side)
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(0, 0, 0);
      doc.text("Teacher's Remarks", leftCol, y);
      y += 5;
      
      doc.setFont('helvetica', 'italic');
      doc.setTextColor(...grayText);
      doc.setFontSize(9);
      const teacherRemarks = formData.footer.teacherRemarks || '';
      const splitTeacherRemarks = doc.splitTextToSize(teacherRemarks, 80);
      doc.text(splitTeacherRemarks, leftCol, y);
      
      // Principal's Remarks (right side)
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(10);
      doc.text("Principal's Remarks", 196, y - 5, { align: 'right' });
      
      doc.setFont('helvetica', 'italic');
      doc.setTextColor(...grayText);
      doc.setFontSize(9);
      const principalRemarks = formData.footer.principalRemarks || '';
      const splitPrincipalRemarks = doc.splitTextToSize(principalRemarks, 80);
      doc.text(splitPrincipalRemarks, 196, y, { align: 'right' });
      
      y += Math.max(splitTeacherRemarks.length, splitPrincipalRemarks.length) * 4 + 10;

      // Signatures
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(10);
      doc.text(formData.footer.teacherName || '', leftCol, y);
      doc.text(formData.footer.principalName || '', 196, y, { align: 'right' });
      y += 4;
      
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...grayText);
      doc.setFontSize(8);
      doc.text("(Teacher's Signature)", leftCol, y);
      doc.text("(Principal's Signature)", 196, y, { align: 'right' });

      doc.save(`${student?.name || 'student'}_holistic_report.pdf`);
      setDebugLogs(prev => [...prev, 'PDF generated and downloaded successfully.']);
      
      toast.success(editingReport ? 'Report card updated successfully!' : 'Report card generated successfully!');
      setShowForm(false);
    } catch (error) {
      console.error("Error generating or saving report card: ", error);
      setDebugLogs(prev => [...prev, `Error: ${error.message}`]);
      toast.error('Failed to save report card: ' + error.message);
    } finally {
      setIsSaving(false);
    }
  };

  if (!showForm) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className={`bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full ${showPreview ? 'max-w-7xl' : 'max-w-4xl'} p-6 max-h-[90vh] overflow-y-auto`}
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            {editingReport ? 'Edit Holistic Report Card' : 'Generate Holistic Report Card'}
          </h2>
          <div className="flex items-center">
            <button onClick={() => setShowPreview(!showPreview)} className="mr-4 text-blue-500">{showPreview ? 'Hide' : 'Show'} Preview</button>
            <button
              onClick={() => setShowForm(false)}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Color Theme Selector */}
        <div className="mb-6 p-4 border rounded-lg bg-gray-50 dark:bg-gray-700">
          <div className="flex items-center mb-3">
            <Palette size={20} className="mr-2 text-gray-600 dark:text-gray-300" />
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Select Color Theme</h3>
          </div>
          <div className="flex flex-wrap gap-3">
            {Object.entries(colorThemes).map(([key, theme]) => (
              <button
                key={key}
                onClick={() => setSelectedTheme(key)}
                className={`flex items-center px-3 py-2 rounded-lg border-2 transition-all ${
                  selectedTheme === key 
                    ? 'border-gray-800 dark:border-white shadow-md' 
                    : 'border-transparent hover:border-gray-300'
                }`}
                title={theme.name}
              >
                <div 
                  className="w-6 h-6 rounded-full mr-2" 
                  style={{ backgroundColor: theme.primaryHex }}
                />
                <div 
                  className="w-4 h-4 rounded-full -ml-4 mt-3 border-2 border-white" 
                  style={{ backgroundColor: theme.secondary }}
                />
                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">{theme.name}</span>
              </button>
            ))}
          </div>
        </div>

        <div className={`grid ${showPreview ? 'grid-cols-2 gap-8' : 'grid-cols-1'}`}>
            <div className="space-y-6">
                {/* Student Details Section */}
                <div id="student-details" className="p-4 border rounded-lg">
                    <h3 className="text-lg font-semibold mb-4">1. Student Details</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Batch</label>
                            <select
                                value={selectedBatch}
                                onChange={handleBatchChange}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                            >
                                <option value="">Select Batch</option>
                                {batches.map(batch => (
                                    <option key={batch.id} value={batch.id}>{batch.name}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Student</label>
                            <select
                                value={formData.studentId}
                                onChange={handleStudentChange}
                                disabled={!selectedBatch || students.length === 0}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                            >
                                <option value="">Select Student</option>
                                {students.map(student => (
                                    <option key={student.id} value={student.id}>{student.name || student.displayName}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Academic Session</label>
                            <input
                                type="text"
                                value={formData.academicSession}
                                onChange={(e) => setFormData({ ...formData, academicSession: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Class</label>
                            <input
                                type="text"
                                value={formData.class}
                                onChange={(e) => setFormData({ ...formData, class: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Section</label>
                            <input
                                type="text"
                                value={formData.section}
                                onChange={(e) => setFormData({ ...formData, section: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Roll Number</label>
                            <input
                                type="text"
                                value={formData.rollNumber}
                                onChange={(e) => setFormData({ ...formData, rollNumber: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Date of Birth</label>
                            <input
                                type="date"
                                value={formData.dateOfBirth}
                                onChange={(e) => setFormData({ ...formData, dateOfBirth: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                            />
                        </div>
                    </div>
                </div>

                {/* Academic Performance Section */}
                <div id="academic-performance" className="p-4 border rounded-lg">
                    <h3 className="text-lg font-semibold mb-4">2. Academic Performance</h3>
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Report Type</label>
                        <select
                            value={formData.reportType}
                            onChange={(e) => setFormData({ ...formData, reportType: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                        >
                            <option value="exam">Exam Report</option>
                            <option value="final">Final Report</option>
                        </select>
                    </div>

                    {formData.reportType === 'exam' ? (
                        <div>
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Exam Name</label>
                                <input
                                    type="text"
                                    value={formData.examName}
                                    onChange={(e) => setFormData({ ...formData, examName: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                                />
                            </div>
                            {formData.subjects.map((subject, index) => (
                                <div key={index} className="grid grid-cols-1 md:grid-cols-5 gap-2 mb-2">
                                    <input type="text" placeholder="Subject Name" value={subject.name} onChange={(e) => { const subjects = [...formData.subjects]; subjects[index].name = e.target.value; setFormData({ ...formData, subjects }); }} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white" />
                                    <input type="number" placeholder="Marks Obtained" value={subject.marks} onChange={(e) => { const subjects = [...formData.subjects]; subjects[index].marks = e.target.value; setFormData({ ...formData, subjects }); }} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white" />
                                    <input type="number" placeholder="Max Marks" value={subject.maxMarks} onChange={(e) => { const subjects = [...formData.subjects]; subjects[index].maxMarks = e.target.value; setFormData({ ...formData, subjects }); }} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white" />
                                    <input type="text" placeholder="Grade" value={subject.grade} onChange={(e) => { const subjects = [...formData.subjects]; subjects[index].grade = e.target.value; setFormData({ ...formData, subjects }); }} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white" />
                                    <div className="flex items-center">
                                        <input type="text" placeholder="Remarks" value={subject.remarks} onChange={(e) => { const subjects = [...formData.subjects]; subjects[index].remarks = e.target.value; setFormData({ ...formData, subjects }); }} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white" />
                                        <button onClick={() => handleRemoveSubject(index)} className="ml-2 text-red-500"><Trash2 size={18} /></button>
                                    </div>
                                </div>
                            ))}
                            <button onClick={handleAddSubject} className="text-blue-500 flex items-center"><Plus size={18} className="mr-1" /> Add Subject</button>
                        </div>
                    ) : (
                        <div>
                            <div className="mb-4">
                                <h4 className="text-md font-semibold mb-2">Exams</h4>
                                {formData.finalReport.exams.map((exam, index) => (
                                    <div key={index} className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-2">
                                        <input type="text" placeholder="Exam Name" value={exam.name} onChange={(e) => { const exams = [...formData.finalReport.exams]; exams[index].name = e.target.value; setFormData({ ...formData, finalReport: { ...formData.finalReport, exams } }); }} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white" />
                                        <input type="number" placeholder="Max Marks" value={exam.maxMarks} onChange={(e) => { const exams = [...formData.finalReport.exams]; exams[index].maxMarks = e.target.value; setFormData({ ...formData, finalReport: { ...formData.finalReport, exams } }); }} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white" />
                                        <button onClick={() => handleRemoveExam(index)} className="text-red-500"><Trash2 size={18} /></button>
                                    </div>
                                ))}
                                <button onClick={handleAddExam} className="text-blue-500 flex items-center"><Plus size={18} className="mr-1" /> Add Exam</button>
                            </div>
                            <div>
                                <h4 className="text-md font-semibold mb-2">Subjects</h4>
                                {formData.finalReport.subjects.map((subject, subIndex) => (
                                    <div key={subIndex} className="grid grid-cols-1 md:grid-cols-12 gap-2 mb-2">
                                        <div className="col-span-2">
                                            <input type="text" placeholder="Subject Name" value={subject.name} onChange={(e) => { const subjects = [...formData.finalReport.subjects]; subjects[subIndex].name = e.target.value; setFormData({ ...formData, finalReport: { ...formData.finalReport, subjects } }); }} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white" />
                                        </div>
                                        {formData.finalReport.exams.map((exam, examIndex) => (
                                            <div key={examIndex} className="col-span-2">
                                                <input type="number" placeholder={`Marks in ${exam.name}`} value={subject.marks[examIndex] || ''} onChange={(e) => { const subjects = [...formData.finalReport.subjects]; subjects[subIndex].marks[examIndex] = e.target.value; setFormData({ ...formData, finalReport: { ...formData.finalReport, subjects } }); }} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white" />
                                            </div>
                                        ))}
                                        <div className="col-span-2">
                                            <input type="number" placeholder="Total Marks" readOnly value={subject.marks.reduce((a, b) => Number(a) + Number(b), 0)} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-100 dark:bg-gray-700" />
                                        </div>
                                        <button onClick={() => handleRemoveSubject(subIndex)} className="text-red-500"><Trash2 size={18} /></button>
                                    </div>
                                ))}
                                <button onClick={handleAddSubject} className="text-blue-500 flex items-center"><Plus size={18} className="mr-1" /> Add Subject</button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Overall Academic Summary Section */}
                <div id="academic-summary" className="p-4 border rounded-lg">
                    <h3 className="text-lg font-semibold mb-4">3. Overall Academic Summary</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Total Marks Obtained</label>
                            <input type="number" readOnly value={formData.summary.totalMarksObtained} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-100 dark:bg-gray-700" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Total Maximum Marks</label>
                            <input type="number" readOnly value={formData.summary.totalMaximumMarks} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-100 dark:bg-gray-700" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Percentage</label>
                            <input type="text" readOnly value={`${formData.summary.percentage}%`} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-100 dark:bg-gray-700" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Overall Grade</label>
                            <input type="text" value={formData.summary.overallGrade} onChange={(e) => setFormData({ ...formData, summary: { ...formData.summary, overallGrade: e.target.value } })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Rank (Optional)</label>
                            <input type="text" value={formData.summary.rank} onChange={(e) => setFormData({ ...formData, summary: { ...formData.summary, rank: e.target.value } })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white" />
                        </div>
                    </div>
                </div>

                {/* Co-Scholastic Areas Section */}
                <div id="co-scholastic" className="p-4 border rounded-lg">
                    <h3 className="text-lg font-semibold mb-4">4. Co-Scholastic Areas (Optional)</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Discipline</label>
                            <input type="text" value={formData.coScholastic.discipline} onChange={(e) => setFormData({ ...formData, coScholastic: { ...formData.coScholastic, discipline: e.target.value } })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Communication Skills</label>
                            <input type="text" value={formData.coScholastic.communicationSkills} onChange={(e) => setFormData({ ...formData, coScholastic: { ...formData.coScholastic, communicationSkills: e.target.value } })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Participation</label>
                            <input type="text" value={formData.coScholastic.participation} onChange={(e) => setFormData({ ...formData, coScholastic: { ...formData.coScholastic, participation: e.target.value } })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Teamwork</label>
                            <input type="text" value={formData.coScholastic.teamwork} onChange={(e) => setFormData({ ...formData, coScholastic: { ...formData.coScholastic, teamwork: e.target.value } })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Leadership</label>
                            <input type="text" value={formData.coScholastic.leadership} onChange={(e) => setFormData({ ...formData, coScholastic: { ...formData.coScholastic, leadership: e.target.value } })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Creativity</label>
                            <input type="text" value={formData.coScholastic.creativity} onChange={(e) => setFormData({ ...formData, coScholastic: { ...formData.coScholastic, creativity: e.target.value } })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white" />
                        </div>
                    </div>
                </div>

                {/* Skills Section */}
                <div id="skills" className="p-4 border rounded-lg">
                    <h3 className="text-lg font-semibold mb-4">5. Skills (Optional)</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Critical Thinking</label>
                            <input type="text" value={formData.skills.criticalThinking} onChange={(e) => setFormData({ ...formData, skills: { ...formData.skills, criticalThinking: e.target.value } })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Problem Solving</label>
                            <input type="text" value={formData.skills.problemSolving} onChange={(e) => setFormData({ ...formData, skills: { ...formData.skills, problemSolving: e.target.value } })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Analytical Skills</label>
                            <input type="text" value={formData.skills.analyticalSkills} onChange={(e) => setFormData({ ...formData, skills: { ...formData.skills, analyticalSkills: e.target.value } })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Collaboration</label>
                            <input type="text" value={formData.skills.collaboration} onChange={(e) => setFormData({ ...formData, skills: { ...formData.skills, collaboration: e.target.value } })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Initiative</label>
                            <input type="text" value={formData.skills.initiative} onChange={(e) => setFormData({ ...formData, skills: { ...formData.skills, initiative: e.target.value } })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white" />
                        </div>
                    </div>
                </div>

                {/* Attendance Section */}
                <div id="attendance" className="p-4 border rounded-lg">
                    <h3 className="text-lg font-semibold mb-4">6. Attendance</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Total Working Days</label>
                            <input type="number" value={formData.attendance.totalWorkingDays} onChange={(e) => setFormData({ ...formData, attendance: { ...formData.attendance, totalWorkingDays: e.target.value } })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Days Present</label>
                            <input type="number" value={formData.attendance.daysPresent} onChange={(e) => setFormData({ ...formData, attendance: { ...formData.attendance, daysPresent: e.target.value } })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Attendance Percentage</label>
                            <input type="text" readOnly value={`${formData.attendance.percentage}%`} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-100 dark:bg-gray-700" />
                        </div>
                    </div>
                </div>

                {/* Footer Section */}
                <div className="p-4 border rounded-lg">
                    <h3 className="text-lg font-semibold mb-4">7. Footer</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Teacher Remarks</label>
                            <textarea value={formData.footer.teacherRemarks} onChange={(e) => setFormData({ ...formData, footer: { ...formData.footer, teacherRemarks: e.target.value } })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Teacher Name (for signature)</label>
                            <input type="text" value={formData.footer.teacherName} onChange={(e) => setFormData({ ...formData, footer: { ...formData.footer, teacherName: e.target.value } })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Principal Remarks</label>
                            <textarea value={formData.footer.principalRemarks} onChange={(e) => setFormData({ ...formData, footer: { ...formData.footer, principalRemarks: e.target.value } })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Principal Name (for signature)</label>
                            <input type="text" value={formData.footer.principalName} onChange={(e) => setFormData({ ...formData, footer: { ...formData.footer, principalName: e.target.value } })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white" />
                        </div>
                    </div>
                </div>

                <div className="flex justify-end">
                    <button 
                        onClick={generatePDF} 
                        className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400"
                        disabled={isSaving}
                    >
                        {isSaving ? 'Saving...' : (editingReport ? 'Update & Download PDF' : 'Generate & Save Report Card')}
                    </button>
                </div>

                {debugLogs.length > 0 && (
                    <div className="mt-4 p-4 border rounded-lg bg-gray-50 dark:bg-gray-700">
                        <h4 className="font-semibold mb-2">Debug Logs:</h4>
                        <pre className="text-xs whitespace-pre-wrap">{debugLogs.join('\n')}</pre>
                    </div>
                )}
            </div>
            {showPreview && (
                <div className="p-4 border rounded-lg">
                    <HolisticReportCardPreview data={formData} student={students.find(s => s.id === formData.studentId)} userProfile={userProfile} theme={colorThemes[selectedTheme]} />
                </div>
            )}
        </div>
      </motion.div>
    </div>
  );
};

export default HolisticReportCardForm;
